import { useEffect, useState, useCallback } from "react";
import { Pencil, Trash2 } from "lucide-react";
import api from "../../../../../utils/axiosInstance";
import FormInput from "../../../../../components/Form/FormInput";
import { btn } from "../../../../../components/Button/buttonStyles";
import { toast, confirmDialog } from "../../../../../components/Toast/toast";
import { formatMoney } from "../../quotationFormat";

const EMPTY_FORM = { name: "", rate: "", note: "" };
const HEADERS = ["Name", "Rate, %", "Amount", "Note", "Actions"];

const TaxesTab = ({ quotationId, locked }) => {
  const [taxes, setTaxes] = useState([]);
  const [totals, setTotals] = useState(null);
  const [loading, setLoading] = useState(true);

  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const applyResponse = (res) => {
    setTaxes(res.data.data || []);
    setTotals(res.data.meta);
  };

  const fetchTaxes = useCallback(async () => {
    try {
      applyResponse(await api.get(`/quotations/${quotationId}/taxes`));
    } catch (err) {
      console.error("Failed to fetch taxes", err);
      toast.error("Failed to fetch taxes");
    } finally {
      setLoading(false);
    }
  }, [quotationId]);

  useEffect(() => {
    fetchTaxes();
  }, [fetchTaxes]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const resetForm = () => {
    setForm(EMPTY_FORM);
    setEditingId(null);
    setErrors({});
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Tax name is required.";
    const rate = Number(form.rate);
    if (form.rate === "" || Number.isNaN(rate) || rate < 0 || rate > 100)
      e.rate = "Enter a rate between 0 and 100.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    const payload = { name: form.name.trim(), rate: Number(form.rate), note: form.note.trim() || null };

    setSaving(true);
    try {
      const res = editingId
        ? await api.put(`/quotations/${quotationId}/taxes/${editingId}`, payload)
        : await api.post(`/quotations/${quotationId}/taxes`, payload);
      applyResponse(res);
      toast.success(res.data.message);
      resetForm();
    } catch (err) {
      console.error("Save tax failed", err);
      toast.error(err.response?.data?.message || "Failed to save tax");
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (tax) => {
    setEditingId(tax.id);
    setForm({ name: tax.name, rate: String(tax.rate), note: tax.note || "" });
    setErrors({});
  };

  const handleDelete = async (tax) => {
    if (!(await confirmDialog(`Remove the tax "${tax.name}"?`))) return;
    try {
      const res = await api.delete(`/quotations/${quotationId}/taxes/${tax.id}`);
      applyResponse(res);
      if (editingId === tax.id) resetForm();
    } catch (err) {
      console.error("Delete tax failed", err);
      toast.error(err.response?.data?.message || "Failed to remove tax");
    }
  };

  const currency = totals?.currency ? ` ${totals.currency}` : "";

  if (loading) {
    return (
      <div className="flex justify-center py-10">
        <div className="animate-spin rounded-full h-8 w-8 border border-gray-300 border-t-brand-600" />
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <section>
        <h2 className="text-xl font-bold text-gray-900 mb-1">Taxes</h2>
        <p className="text-sm text-gray-500 mb-3">
          Each tax is a percentage of the quotation's receivables subtotal.
        </p>

        {!locked && (
        <form onSubmit={handleSubmit} noValidate className="bg-white shadow rounded-lg p-5">
          <div className="grid grid-cols-1 md:grid-cols-[2fr_1fr_3fr_auto] gap-4 items-start">
            <FormInput
              label="Name"
              name="name"
              value={form.name}
              onChange={handleChange}
              error={errors.name}
              placeholder="e.g. GST"
              required
            />
            <FormInput
              label="Rate, %"
              name="rate"
              type="number"
              value={form.rate}
              onChange={handleChange}
              error={errors.rate}
              required
            />
            <FormInput label="Note" name="note" value={form.note} onChange={handleChange} />
            <div className="flex gap-2 md:pt-6">
              <button type="submit" disabled={saving} className={btn("primary", "sm")}>
                {saving ? "Saving..." : editingId ? "Save" : "Add Tax"}
              </button>
              {editingId && (
                <button type="button" onClick={resetForm} className={btn("secondary", "sm")}>
                  Cancel
                </button>
              )}
            </div>
          </div>
        </form>
        )}
      </section>

      <section className="w-full bg-white shadow rounded-lg overflow-hidden">
        <table className="w-full table-auto border-collapse">
          <thead>
            <tr className="bg-white border-b text-[10px] tracking-widest text-gray-500 uppercase">
              {HEADERS.map((h) => (
                <th key={h} className="px-3 py-2 text-left font-semibold whitespace-nowrap">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {taxes.length === 0 ? (
              <tr>
                <td colSpan={HEADERS.length} className="py-10 text-center text-gray-400 text-xs">
                  No taxes on this quotation.
                </td>
              </tr>
            ) : (
              taxes.map((tax, index) => (
                <tr
                  key={tax.id}
                  className={`${
                    editingId === tax.id ? "bg-brand-50" : index % 2 === 0 ? "bg-gray-50" : "bg-white"
                  } hover:bg-brand-50 transition-colors`}
                >
                  <td className="px-3 py-2 text-xs text-gray-900 font-medium">{tax.name}</td>
                  <td className="px-3 py-2 text-xs text-gray-700">{tax.rate}</td>
                  <td className="px-3 py-2 text-xs text-gray-700 whitespace-nowrap">
                    {formatMoney(tax.amount)}
                    {currency}
                  </td>
                  <td className="px-3 py-2 text-xs text-gray-600 break-words">{tax.note || "—"}</td>
                  <td className="px-3 py-2">
                    <div className="flex gap-3">
                      {locked ? (
                        <span className="text-gray-300 text-xs">—</span>
                      ) : (
                        <>
                          <Pencil
                            className="w-4 h-4 text-brand-400 hover:text-brand-600 cursor-pointer transition-colors"
                            title="Edit"
                            onClick={() => handleEdit(tax)}
                          />
                          <Trash2
                            className="w-4 h-4 text-danger-400 hover:text-danger-600 cursor-pointer transition-colors"
                            title="Remove"
                            onClick={() => handleDelete(tax)}
                          />
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </section>

      {totals && (
        <section className="flex justify-end">
          <div className="w-full sm:w-80 bg-white shadow rounded-lg p-4 space-y-2 text-sm">
            <div className="flex justify-between text-gray-600">
              <span>Subtotal</span>
              <span>{formatMoney(totals.subtotal)}{currency}</span>
            </div>
            <div className="flex justify-between text-gray-600">
              <span>Taxes</span>
              <span>{formatMoney(totals.tax_total)}{currency}</span>
            </div>
            <div className="flex justify-between border-t pt-2 font-semibold text-gray-900">
              <span>Total</span>
              <span>{formatMoney(totals.total)}{currency}</span>
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default TaxesTab;
